<!--
// app/components/pages/region-tables/GridPlayoffPhase.vue
-->
<script setup lang="ts">
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import type { Team } from "~/src/team/domain/models/Team";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { teamArrToObjById } from "~/src/team/domain/services/teamService";
import { useSeasons } from "~/app/composables/season/seasons";
import { setAliasUrl, sortItemsByParam } from "~/src/Shared/utils";

type Props = {
  playoffPhase: Phase | undefined;
  seasonId: number | undefined;
  winnerTeamId: number | null;
};

const props = defineProps<Props>();

const { currentSeason } = useCompetitionStore();
const { getSeasonTeams } = useSeasons();

const teams = ref<Record<number, Team> | undefined>();

const seasonTeams = computed(() => {
  return !!props.playoffPhase ? props.playoffPhase?.groups[0].teams : undefined;
});

/*const playoffTurns = computed(() => {
  return props.playoffPhase?.groups.map(group => ({
    ...group,
    turns:
  }));
});*/

watch(
  seasonTeams,
  async newTeams => {
    if (newTeams) {
      const theTeams = await teamArrToObjById(newTeams);
      teams.value = theTeams || undefined;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div :key="`season-${seasonId}`" v-if="playoffPhase && teams" class="mt-6 md:mt-8">
    <div class="pb-2">
      <p class="kql-page-section-title">Playoff</p>
      <hr />
    </div>

    <div v-for="group in playoffPhase.groups" class="mb-4 md:text-center">
      <div class="max-w-full overflow-x-scroll hide-scrollbar">
        <div class="mt-4 grid auto-cols-min grid-flow-col gap-5 max-w-max mx-auto">
          <div v-for="turn in sortItemsByParam([...group.turns], 'start')" class="flex flex-col">
            <p class="font-bold text-lg md:text-xl text-center mb-2">{{ turn.name }}</p>
            <div v-if="!!teams" class="flex flex-col items-center justify-center gap-2 flex-1">
              <div
                v-for="match in turn.matches"
                class="bg-gray-800 rounded-md px-2 md:px-4 py-2 grid grid-cols-[1fr_auto_1fr] items-center justify-items-center min-w-[200px] md:min-w-[250px]"
              >
                <nuxt-img
                  v-if="match?.participants?.homeTeamId && !!teams[match.participants.homeTeamId]"
                  :src="setAliasUrl(teams[match.participants.homeTeamId].logo?.url) || ''"
                  :alt="teams[match.participants.homeTeamId].completeName || ''"
                  class="max-w-11 h-11 object-contain"
                  height="44"
                  width="44"
                  loading="lazy"
                />
                <span
                  v-else
                  class="text-sm font-bold text-gray-400 w-11 h-11 flex items-center justify-center bg-gray-700 bg-opacity-20 rounded-full"
                >
                  ?
                </span>

                <div class="flex items-baseline justify-center">
                  <span
                    v-if="typeof match.scores.homeScoreP === 'number'"
                    class="font-bold text-sm text-primary mx-1"
                  >
                    {{ match.scores.homeScoreP }}
                  </span>
                  <p class="font-bold text-lg" :class="{ 'ml-1': !match.scores.homeScoreP }">
                    {{ match.scores.homeScore === null ? "-" : match.scores.homeScore }}
                  </p>
                  <p class="text-gray-400 text-xs lg:text-sm lg:mx-3.5 mx-1 self-center">VS</p>
                  <p class="font-bold text-lg" :class="{ 'mr-1': !match.scores.awayScoreP }">
                    {{ match.scores.awayScore === null ? "-" : match.scores.awayScore }}
                  </p>
                  <span
                    v-if="typeof match.scores.awayScoreP === 'number'"
                    class="font-bold text-sm text-primary mx-1"
                  >
                    {{ match.scores.awayScoreP }}
                  </span>
                </div>

                <nuxt-img
                  v-if="match?.participants?.awayTeamId && !!teams[match.participants.awayTeamId]"
                  :src="setAliasUrl(teams[match.participants.awayTeamId].logo?.url) || ''"
                  :alt="teams[match.participants.awayTeamId].completeName || ''"
                  class="max-w-11 h-11 object-contain"
                  height="44"
                  width="44"
                  loading="lazy"
                />
                <span
                  v-else
                  class="text-sm font-bold text-gray-400 w-11 h-11 flex items-center justify-center bg-gray-700 bg-opacity-20 rounded-full"
                >
                  ?
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="winnerTeamId && !!teams[winnerTeamId]" class="mb-6 flex items-center gap-1 md:justify-center">
      {{ $t("team.historicPlayoff.winner") }}:
      <div class="flex items-center justify-start gap-0.5">
        <nuxt-img
          :src="setAliasUrl(teams[winnerTeamId].logo.url) || ''"
          :alt="teams[winnerTeamId].shortName || ''"
          class="max-w-11 h-11 object-contain"
          height="44"
          width="44"
          loading="lazy"
        />
        <span class="font-bold text-lg">{{ teams[winnerTeamId].completeName }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
